/**
 * `/sites/:siteId` — site detail with tabbed Pages | Domains.
 *
 * The Pages tab lists pages (path, type, createdAt) with a delete action;
 * the Domains tab lists domains, supports the "Set as primary" toggle, and
 * exposes a delete action.
 *
 * Slice 2 will add the page-version editor; this slice is read + create +
 * delete only.
 */

import { useState } from "react";
import type { ReactElement, SyntheticEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { DomainResponse, PageResponse, SiteResponse } from "@ottopilot/shared";
import { useSite } from "../hooks/useSites";
import { useDeletePage, usePages } from "../hooks/usePages";
import {
  useDeleteDomain,
  useDomains,
  useServerInfo,
  useUpdateDomain,
  useVerifyDomain,
} from "../hooks/useDomains";
import { CreatePageDialog } from "../features/pages/CreatePageDialog";
import { CreateDomainDialog } from "../features/domains/CreateDomainDialog";
import { ThemeTab } from "../features/sites/ThemeTab";
import { ContentItemsTable } from "../features/content/ContentItemsTable";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";
import { buildPublicUrl, findPrimaryDomain } from "../lib/publicUrl";

type TabKey = "pages" | "domains" | "theme" | "content";

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

function PagesTab({ site }: { site: SiteResponse }): ReactElement {
  const [createOpen, setCreateOpen] = useState(false);
  const pages = usePages(site.id, { page: 1, pageSize: 50 });
  const deletePage = useDeletePage();
  const toast = useToast();
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();
  // #184: preview links need the primary domain. The TanStack Query cache
  // de-dupes this read against the same `useDomains` call in the DomainsTab
  // and in the parent header, so there is no extra request.
  const domains = useDomains(site.id);
  const primaryDomain = findPrimaryDomain(domains.data);

  const handleDelete = async (page: PageResponse) => {
    const ok = await confirm({
      title: `Delete page ${page.path}?`,
      body: `Delete page ${page.path}? This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    deletePage.mutate(
      { id: page.id },
      {
        onSuccess: () => toast.showSuccess(`Deleted ${page.path}.`),
        onError: (err) => toast.showError(err.message || "Could not delete page."),
      },
    );
  };

  return (
    <Stack spacing={2}>
      {confirmDialog}
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          data-testid="pages-tab-new-page-button"
        >
          New page
        </Button>
      </Stack>
      {pages.isError ? <Alert severity="error">{pages.error.message}</Alert> : null}
      <Paper variant="outlined">
        <TableContainer>
          <Table aria-label="Pages" data-testid="pages-tab-table">
            <TableHead>
              <TableRow>
                <TableCell>Path</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.isPending ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : pages.data && pages.data.data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{ py: 6 }}
                    data-testid="pages-tab-empty-state"
                  >
                    <Typography variant="body2" color="text.secondary">
                      No pages yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pages.data?.data.map((page) => (
                  <TableRow key={page.id} hover data-testid="pages-tab-row" data-page-id={page.id}>
                    <TableCell data-testid="pages-tab-row-path">
                      {/*
                       * Route to the PageVersion editor (#172). Using a
                       * TanStack Router `<Link>` rather than an anchor so
                       * the SPA shell stays mounted (no full page reload)
                       * and the typed params API keeps the page-id type
                       * honest at the call site.
                       */}
                      <Link
                        to="/sites/$siteId/pages/$pageId"
                        params={{ siteId: site.id, pageId: page.id }}
                        style={{ textDecoration: "none", color: "inherit" }}
                        data-testid="pages-tab-row-path-link"
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {page.path}
                        </Typography>
                      </Link>
                    </TableCell>
                    <TableCell data-testid="pages-tab-row-type">
                      <Chip size="small" label={page.type} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(page.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {primaryDomain ? (
                        <Tooltip title={`Preview at ${primaryDomain.hostname}${page.path}`}>
                          <IconButton
                            aria-label={`Preview ${page.path}`}
                            component="a"
                            href={buildPublicUrl(primaryDomain.hostname, page.path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="pages-tab-row-preview-button"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Delete">
                        <IconButton
                          aria-label={`Delete ${page.path}`}
                          onClick={() => {
                            void handleDelete(page);
                          }}
                          disabled={deletePage.isPending}
                          data-testid="pages-tab-row-delete-button"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <CreatePageDialog open={createOpen} onClose={() => setCreateOpen(false)} siteId={site.id} />
    </Stack>
  );
}

function DomainsTab({ site }: { site: SiteResponse }): ReactElement {
  const [createOpen, setCreateOpen] = useState(false);
  const domains = useDomains(site.id);
  const updateDomain = useUpdateDomain();
  const deleteDomain = useDeleteDomain();
  const verifyDomain = useVerifyDomain();
  const serverInfo = useServerInfo();
  const toast = useToast();
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  // Custom-domain P3 (issue #293). Every new domain lands as
  // PENDING_VERIFICATION (P1, #291) and only flips to ACTIVE after the
  // operator (or eventually customer) clicks Verify and the server
  // confirms DNS A records resolve to VPS_PUBLIC_IP. The banner + verify
  // affordance below only render when at least one row needs it.
  const pendingDomains = (domains.data ?? []).filter((d) => d.status === "PENDING_VERIFICATION");
  const vpsPublicIp = serverInfo.data?.vpsPublicIp;

  /**
   * Bidirectional toggle. Setting `isPrimary: true` auto-demotes the
   * existing primary on the server side (the partial unique index in
   * the schema enforces "at most one primary per site"); setting
   * `isPrimary: false` clears the primary explicitly — the site is
   * then left with no primary domain (also valid; the URL builder
   * just hides Preview affordances until one's set again).
   */
  const handleTogglePrimary = (domain: DomainResponse) => {
    const next = !domain.isPrimary;
    updateDomain.mutate(
      { id: domain.id, input: { isPrimary: next } },
      {
        onSuccess: () =>
          toast.showSuccess(
            next
              ? `${domain.hostname} is now the primary domain.`
              : `${domain.hostname} is no longer the primary domain.`,
          ),
        onError: (err) => toast.showError(err.message || "Could not update primary domain."),
      },
    );
  };

  const handleDelete = async (domain: DomainResponse) => {
    const ok = await confirm({
      title: `Remove ${domain.hostname}?`,
      body: `Remove ${domain.hostname}?`,
      confirmLabel: "Remove",
    });
    if (!ok) return;
    deleteDomain.mutate(
      { id: domain.id },
      {
        onSuccess: () => toast.showSuccess(`Removed ${domain.hostname}.`),
        onError: (err) => toast.showError(err.message || "Could not remove domain."),
      },
    );
  };

  const handleVerify = (domain: DomainResponse) => {
    verifyDomain.mutate(
      { id: domain.id },
      {
        onSuccess: (result) => {
          if (result.ok) {
            toast.showSuccess(
              `${domain.hostname} verified — DNS points to this server. The site will be reachable shortly.`,
            );
          } else {
            // DNS-miss verdict: a normal product outcome, not an error.
            // Surface the diff so the operator can fix at the DNS layer.
            toast.showError(result.message);
          }
        },
        onError: (err) =>
          toast.showError(err.message || "Could not run verification — please try again."),
      },
    );
  };

  return (
    <Stack spacing={2}>
      {confirmDialog}
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          data-testid="domains-tab-new-domain-button"
        >
          New domain
        </Button>
      </Stack>
      {domains.isError ? (
        <Alert severity="error" data-testid="domains-tab-error-alert">
          {domains.error.message}
        </Alert>
      ) : null}
      {pendingDomains.length > 0 && vpsPublicIp ? (
        <Alert severity="info" data-testid="domains-tab-dns-instructions">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            One or more domains are awaiting DNS verification.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            For each pending hostname below, create an A record pointing to{" "}
            <Box
              component="code"
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: "action.hover",
                borderRadius: 0.5,
                fontFamily: "monospace",
              }}
              data-testid="domains-tab-dns-instructions-ip"
            >
              {vpsPublicIp}
            </Box>
            . Then click <strong>Verify</strong> on the row. DNS propagation typically takes a few
            minutes; longer if your provider caches aggressively.
          </Typography>
        </Alert>
      ) : null}
      <Paper variant="outlined">
        <TableContainer>
          <Table aria-label="Domains" data-testid="domains-tab-table">
            <TableHead>
              <TableRow>
                <TableCell>Hostname</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Primary</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {domains.isPending ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : domains.data && domains.data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 6 }}
                    data-testid="domains-tab-empty-state"
                  >
                    <Typography variant="body2" color="text.secondary">
                      No domains yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                domains.data?.map((domain) => (
                  <TableRow
                    key={domain.id}
                    hover
                    data-testid="domains-tab-row"
                    data-domain-id={domain.id}
                  >
                    <TableCell data-testid="domains-tab-row-hostname">
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {domain.hostname}
                      </Typography>
                    </TableCell>
                    <TableCell data-testid="domains-tab-row-status">
                      <Chip
                        size="small"
                        label={
                          domain.status === "ACTIVE"
                            ? "Active"
                            : domain.status === "PENDING_VERIFICATION"
                              ? "Awaiting DNS"
                              : "Inactive"
                        }
                        color={
                          domain.status === "ACTIVE"
                            ? "success"
                            : domain.status === "PENDING_VERIFICATION"
                              ? "warning"
                              : "default"
                        }
                        variant={domain.status === "ACTIVE" ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={domain.isPrimary}
                            onChange={() => handleTogglePrimary(domain)}
                            disabled={updateDomain.isPending}
                            color="success"
                            inputProps={{
                              "aria-label": domain.isPrimary
                                ? `Demote ${domain.hostname} from primary`
                                : `Promote ${domain.hostname} to primary`,
                            }}
                            data-testid="domains-tab-row-primary-toggle"
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            color={domain.isPrimary ? "success.main" : "text.secondary"}
                            sx={{ fontWeight: domain.isPrimary ? 600 : 400 }}
                          >
                            Primary
                          </Typography>
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(domain.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {domain.status === "PENDING_VERIFICATION" ? (
                          <Tooltip title="Re-check DNS and activate if it now points here">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CheckCircleIcon fontSize="small" />}
                              onClick={() => handleVerify(domain)}
                              disabled={
                                verifyDomain.isPending && verifyDomain.variables?.id === domain.id
                              }
                              data-testid="domains-tab-row-verify-button"
                            >
                              Verify
                            </Button>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Remove">
                          <IconButton
                            aria-label={`Remove ${domain.hostname}`}
                            onClick={() => {
                              void handleDelete(domain);
                            }}
                            disabled={deleteDomain.isPending}
                            data-testid="domains-tab-row-delete-button"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <CreateDomainDialog open={createOpen} onClose={() => setCreateOpen(false)} siteId={site.id} />
    </Stack>
  );
}

export function SiteDetailPage(): ReactElement {
  // Route id is `/authenticated/sites/$siteId` because the route lives
  // beneath the pathless `authenticated` layout route (see router.tsx).
  const { siteId } = useParams({ from: "/authenticated/sites/$siteId" });
  const site = useSite(siteId);
  // Tab state lives in the URL so refresh / share lands the operator
  // back on the same tab. `validateSearch` on the route coerces unknown
  // values to `undefined`; we surface `pages` as the default landing
  // tab when no `?tab=` is present.
  const search = useSearch({ from: "/authenticated/sites/$siteId" });
  const navigate = useNavigate();
  const tab: TabKey = search.tab ?? "pages";
  const setTab = (next: TabKey): void => {
    void navigate({
      to: "/sites/$siteId",
      params: { siteId },
      search: { tab: next },
      // `replace: true` keeps the browser back stack clean — hopping
      // tabs shouldn't pollute history.
      replace: true,
    });
  };
  // #184: surface a single-click "Open site" link in the header when a
  // primary domain exists. Cache-shared with the DomainsTab + PagesTab
  // `useDomains` calls below.
  const domains = useDomains(siteId);
  const primaryDomain = findPrimaryDomain(domains.data);

  if (site.isPending) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (site.isError) {
    return <Alert severity="error">{site.error.message}</Alert>;
  }

  if (!site.data) {
    return <Alert severity="warning">Site not found.</Alert>;
  }

  return (
    <Stack spacing={3} data-testid="site-detail-page">
      <Box>
        <Typography variant="body2" color="text.secondary">
          <Link to="/sites">Sites</Link> / {site.data.name}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mt: 1 }}
        >
          <Typography variant="h2" data-testid="site-detail-name">
            {site.data.name}
          </Typography>
          <Chip
            size="small"
            label={site.data.status}
            variant="outlined"
            data-testid="site-detail-status-chip"
          />
          {primaryDomain ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon fontSize="small" />}
              component="a"
              href={buildPublicUrl(primaryDomain.hostname, "/")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="site-detail-open-site-button"
            >
              Open site
            </Button>
          ) : null}
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5 }}
          data-testid="site-detail-slug"
        >
          /{site.data.slug}
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tab}
          onChange={(_e: SyntheticEvent, value: TabKey) => setTab(value)}
          aria-label="Site sections"
        >
          <Tab label="Pages" value="pages" data-testid="site-detail-tab-pages" />
          <Tab label="Domains" value="domains" data-testid="site-detail-tab-domains" />
          <Tab label="Content" value="content" data-testid="site-detail-tab-content" />
          <Tab label="Theme" value="theme" data-testid="site-detail-tab-theme" />
        </Tabs>
      </Box>

      {tab === "pages" ? <PagesTab site={site.data} /> : null}
      {tab === "domains" ? <DomainsTab site={site.data} /> : null}
      {tab === "content" ? <ContentItemsTable siteId={site.data.id} /> : null}
      {tab === "theme" ? <ThemeTab site={site.data} /> : null}
    </Stack>
  );
}
